import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { useRouter } from 'next/router'
import {
    AnimatePresence,
    m,
    MotionConfig,
    LazyMotion,
    domAnimation,
    cubicBezier
} from 'framer-motion'
import clsx from 'clsx'
import { Header } from '@/components'
import { useIsomorphicLayoutEffect } from '@/hooks'
import { fetcher } from '@/libs/utils'
import type { PersonType } from '@/@types/microcms-type'

export const Layout = ({ children }: { children: React.ReactNode }) => {
    const router = useRouter()
    const isHome = router.pathname === '/' || router.pathname === '/[lang]'

    useIsomorphicLayoutEffect(() => {
        if (typeof document !== 'undefined') {
            isHome
                ? document.documentElement.classList.add('overflow-hidden')
                : document.documentElement.classList.remove('overflow-hidden')
        }
    }, [isHome])

    const [isHistoryBack, setIsHistoryBack] = useState(false)

    useEffect(() => {
        router.beforePopState(() => {
            setIsHistoryBack(true)
            return true
        })
    }, [router])

    const { data: people, error } = useSWR<PersonType[]>('/api/people', fetcher)
    if (error) console.error(error)

    const [variants, setVariants] = useState({})

    useEffect(() => {
        setVariants({
            initial: {
                opacity: 0,
                y: 0
            },
            animate: {
                opacity: 1,
                y: 0,
                transition: {
                    duration: 0.38,
                    delay: isHome ? 0.45 : 0
                }
            },
            exit: {
                opacity: 0,
                y: isHome ? 0 : 14,
                transition: {
                    duration: 0.38,
                    delay: 0
                }
            }
        })
    }, [isHome])

    const easeing = cubicBezier(0.42, 0.1, 0.11, 0.99)

    // Set scroll={false} on the <Link/> to prevent scrolling to the top when exiting a page.
    // Then, utilize AnimatePresence to scroll to the top once the exit animation is complete.
    // If it's history back, don't scroll to the top.
    const handleScroll = () => {
        if (!isHistoryBack && typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'auto' })
        }
        setIsHistoryBack(false)
    }

    return (
        <>
            {people && <Header people={people} />}
            <MotionConfig
                transition={{
                    ease: easeing
                }}
            >
                <LazyMotion features={domAnimation}>
                    <AnimatePresence mode="wait" onExitComplete={handleScroll}>
                        <m.div
                            key={router.asPath}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            variants={variants}
                        >
                            <main
                                id="main"
                                className={clsx(
                                    'transform-opacity duration-[1000ms]',
                                    isHome &&
                                        'w-full h-full overflow-hidden hidden-scrollbar [&_*]:hidden-scrollbar'
                                )}
                            >
                                {children}
                            </main>
                        </m.div>
                    </AnimatePresence>
                </LazyMotion>
            </MotionConfig>
        </>
    )
}
